/**
 * Settings Section Configs
 *
 * Each config defines:
 *   - key: URL-friendly section key (matches backend SECTION_MAP)
 *   - label: Display name
 *   - description: Short description for the landing page card
 *   - columns: Ant Design Table column definitions
 *   - formFields: Field definitions for the Add/Edit modal form
 *
 * formField types: "text", "select", "multiselect", "number", "boolean", "textarea"
 */

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

/**
 * Helper — creates a render function that maps raw values to display labels.
 */
const labelRenderer = (options) => (value) => {
  const match = options.find((o) => o.value === value);
  return match ? match.label : value || "—";
};

const boolRenderer = (value) => (value ? "Yes" : "No");

/**
 * Helper — same as labelRenderer, for array-valued fields.
 */
const multiLabelRenderer = (options) => (values) => {
  if (!Array.isArray(values) || !values.length) return "—";
  return values
    .map((v) => options.find((o) => o.value === v)?.label || v)
    .join(", ");
};

const CATEGORY_OPTIONS = [
  { label: "Fleet", value: "fleet" },
  { label: "Vehicle", value: "vehicle" },
];

const DESIGNATION_TYPE_OPTIONS = [
  { label: "Admin", value: "admin" },
  { label: "Rider", value: "rider" },
];

const EXPENSE_CATEGORY_OPTIONS = [
  { label: "Rider", value: "rider" },
  { label: "Company", value: "company" },
];

const EXPENSE_NATURE_OPTIONS = [
  { label: "Deduction", value: "deduction" },
  { label: "Benefit", value: "benefit" },
  { label: "COD Deduction", value: "codDeduction" },
  { label: "Advance COD Deduction", value: "advanceCodDeduction" },
  { label: "Fuel Top-up", value: "fuelTopup" },
  { label: "Fuel Used", value: "fuelUsed" },
  { label: "Partner Spend", value: "partnerSpend" },
  { label: "Other", value: "other" },
];

const BANK_ACCOUNT_TYPE_OPTIONS = [
  { label: "CoD Bank Account", value: "cod" },
  { label: "Company Bank Account", value: "company" },
];

const SCHEME_TYPE_OPTIONS = [
  { label: "Fixed", value: "fixed" },
  { label: "Variable", value: "variable" },
  { label: "Hybrid", value: "hybrid" },
];

const INCENTIVE_TYPE_OPTIONS = [
  { label: "Performance", value: "performance" },
  { label: "Referral", value: "referral" },
  { label: "Biller", value: "biller" },
];

const MODULE_OPTIONS = [
  { label: "Delivery Partners", value: "deliveryPartners" },
  { label: "Transactions", value: "transactions" },
  { label: "Cash & COD", value: "cashCOD" },
  { label: "Payroll & Expenses", value: "payrollExpenses" },
  { label: "Payouts", value: "payouts" },
  { label: "Users & Leaves", value: "usersLeaves" },
  { label: "Accounts", value: "accounts" },
];

const sectionConfigs = {
  projects: {
    key: "projects",
    label: "Project Mapping",
    description: "Create projects and assign users/riders",
    columns: [
      { title: "Project Name", dataIndex: "name", sorter: true },
      { title: "Code", dataIndex: "code", sorter: true },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Project Name", type: "text", required: true },
      { name: "code", label: "Code", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "fleet-vehicle-types": {
    key: "fleet-vehicle-types",
    label: "Fleet & Vehicle Types",
    description: "Fleet categories and vehicle types",
    columns: [
      { title: "Type Name", dataIndex: "name", sorter: true },
      { title: "Category", dataIndex: "category", sorter: true, render: labelRenderer(CATEGORY_OPTIONS) },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Type Name", type: "text", required: true },
      { name: "category", label: "Category", type: "select", required: true, options: CATEGORY_OPTIONS },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  designations: {
    key: "designations",
    label: "Designations",
    description: "Admin and rider job titles",
    columns: [
      { title: "Designation Name", dataIndex: "name", sorter: true },
      { title: "Type", dataIndex: "designationType", sorter: true, render: labelRenderer(DESIGNATION_TYPE_OPTIONS) },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Designation Name", type: "text", required: true },
      { name: "designationType", label: "Type", type: "select", required: true, options: DESIGNATION_TYPE_OPTIONS },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "city-zones": {
    key: "city-zones",
    label: "City & Zone",
    description: "City master list and zone definitions linked to cities",
    isCustomPage: true, // Uses CityZonePage instead of generic
    columns: [],
    formFields: [],
  },

  "salary-schemes": {
    key: "salary-schemes",
    label: "Salary Scheme",
    description: "Salary calculation scheme definitions",
    columns: [
      { title: "Scheme Name", dataIndex: "name", sorter: true },
      { title: "Scheme Type", dataIndex: "schemeType", sorter: true, render: labelRenderer(SCHEME_TYPE_OPTIONS) },
      { title: "Base Salary (OMR)", dataIndex: "baseSalary", sorter: true },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Scheme Name", type: "text", required: true },
      { name: "schemeType", label: "Scheme Type", type: "select", required: true, options: SCHEME_TYPE_OPTIONS },
      { name: "baseSalary", label: "Base Salary (OMR)", type: "number" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "leave-types": {
    key: "leave-types",
    label: "Leave Type",
    description: "Leave types for rider applications",
    columns: [
      { title: "Leave Type", dataIndex: "name", sorter: true },
      { title: "Max Days/Year", dataIndex: "maxDaysPerYear", sorter: true },
      { title: "Paid", dataIndex: "isPaid", render: (v) => (v ? "Yes" : "No") },
      { title: "Carry Forward", dataIndex: "carryForward", render: (v) => (v ? "Yes" : "No") },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Leave Type Name", type: "text", required: true },
      { name: "maxDaysPerYear", label: "Max Days Per Year", type: "number", required: true },
      { name: "isPaid", label: "Paid Leave", type: "boolean" },
      { name: "carryForward", label: "Carry Forward", type: "boolean" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "bank-details": {
    key: "bank-details",
    label: "Bank Details",
    description: "Bank accounts for rider payouts and COD deposits",
    columns: [
      { title: "Bank Name", dataIndex: "name", sorter: true },
      { title: "Account No", dataIndex: "accountNo" },
      { title: "IBAN", dataIndex: "iban" },
      { title: "Branch", dataIndex: "branch" },
      { title: "Display Name", dataIndex: "displayName" },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Bank Name", type: "text", required: true },
      { name: "accountNo", label: "Account Number", type: "text", required: true },
      { name: "iban", label: "IBAN", type: "text" },
      { name: "branch", label: "Branch", type: "text" },
      { name: "displayName", label: "Display Name (shown in mobile)", type: "text" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "expense-emi-types": {
    key: "expense-emi-types",
    label: "Expense & EMI Types",
    description: "Expense categories and EMI expense types",
    columns: [
      { title: "Name", dataIndex: "name", sorter: true },
      { title: "Category", dataIndex: "expenseCategory", sorter: true, render: labelRenderer(EXPENSE_CATEGORY_OPTIONS) },
      { title: "GL Code", dataIndex: "glCode" },
      { title: "Nature", dataIndex: "expenseNature", sorter: true, render: labelRenderer(EXPENSE_NATURE_OPTIONS) },
      { title: "EMI Eligible", dataIndex: "emiEligible", render: boolRenderer },
      { title: "Order Number", dataIndex: "displayOrder", sorter: true },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "expenseCategory", label: "Category", type: "select", required: true, options: EXPENSE_CATEGORY_OPTIONS },
      { name: "glCode", label: "GL Code", type: "text" },
      { name: "expenseNature", label: "Nature", type: "select", required: true, options: EXPENSE_NATURE_OPTIONS, defaultValue: "other" },
      { name: "emiEligible", label: "EMI Eligible", type: "boolean", defaultValue: false },
      { name: "displayOrder", label: "Order Number", type: "number" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "payment-modes": {
    key: "payment-modes",
    label: "Payment Modes",
    description: "Cash, Bank Transfer, Card, CDM, etc.",
    columns: [
      { title: "Mode Name", dataIndex: "name", sorter: true },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Mode Name", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  reasons: {
    key: "reasons",
    label: "Reasons",
    description: "Standard rejection/hold reasons for all modules",
    columns: [
      { title: "Reason Text", dataIndex: "name", sorter: true },
      { title: "Applicable Module", dataIndex: "applicableModule", sorter: true, render: labelRenderer(MODULE_OPTIONS) },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Reason Text", type: "text", required: true },
      { name: "applicableModule", label: "Applicable Module", type: "select", required: true, options: MODULE_OPTIONS },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "insurance-types": {
    key: "insurance-types",
    label: "Insurance Type",
    description: "Vehicle insurance types",
    columns: [
      { title: "Insurance Type", dataIndex: "name", sorter: true },
      { title: "Coverage Description", dataIndex: "coverageDescription" },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Insurance Type Name", type: "text", required: true },
      { name: "coverageDescription", label: "Coverage Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  aggregators: {
    key: "aggregators",
    label: "Aggregators",
    description: "Delivery aggregator master list",
    columns: [
      { title: "Aggregator Name", dataIndex: "name", sorter: true },
      { title: "Code", dataIndex: "aggregatorCode" },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Aggregator Name", type: "text", required: true },
      { name: "aggregatorCode", label: "Code", type: "text" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "incentive-billers": {
    key: "incentive-billers",
    label: "Incentives & Billers",
    description: "Incentive names and billing categories",
    columns: [
      { title: "Name", dataIndex: "name", sorter: true },
      { title: "Incentive Type", dataIndex: "incentiveType", sorter: true, render: labelRenderer(INCENTIVE_TYPE_OPTIONS) },
      { title: "Amount (OMR)", dataIndex: "amount" },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Name", type: "text", required: true },
      { name: "incentiveType", label: "Incentive Type", type: "select", options: INCENTIVE_TYPE_OPTIONS },
      { name: "amount", label: "Amount (OMR)", type: "number" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  nationalities: {
    key: "nationalities",
    label: "Nationality",
    description: "Rider nationalities for the Add Rider dropdown",
    columns: [
      { title: "Nationality", dataIndex: "name", sorter: true },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Nationality", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "noc-types": {
    key: "noc-types",
    label: "NOC Types",
    description: "No Objection Certificate types and their PDF body text",
    columns: [
      { title: "NOC Type", dataIndex: "name", sorter: true },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "NOC Type", type: "text", required: true },
      { name: "bodyTemplate", label: "PDF Body Text", type: "textarea" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "noc-categories": {
    key: "noc-categories",
    label: "NOC Categories",
    description: "Categories used to classify issued NOC certificates",
    columns: [
      { title: "NOC Category", dataIndex: "name", sorter: true },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "NOC Category", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "work-types": {
    key: "work-types",
    label: "Work Types",
    description: "Rider work types used by EMI agreements (e.g. Bike Rider, Car Rider)",
    columns: [
      { title: "Work Type", dataIndex: "name", sorter: true },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Work Type", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "biller-categories": {
    key: "biller-categories",
    label: "Biller Categories",
    description: "Expense categories for company billing (Accounts module)",
    columns: [
      { title: "Category", dataIndex: "name", sorter: true },
      { title: "GL Code", dataIndex: "glCode" },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Category", type: "text", required: true },
      { name: "glCode", label: "GL Code", type: "text" },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  "company-bank-accounts": {
    key: "company-bank-accounts",
    label: "Bank Account",
    description: "Company accounts — CoD accounts show in the rider app, Company accounts print on invoices",
    columns: [
      { title: "Account Name", dataIndex: "name", sorter: true },
      { title: "Bank", dataIndex: "bankName", sorter: true },
      { title: "Account No", dataIndex: "accountNo" },
      { title: "IBAN", dataIndex: "iban" },
      { title: "Account Type", dataIndex: "accountTypes", render: multiLabelRenderer(BANK_ACCOUNT_TYPE_OPTIONS) },
      { title: "Status", dataIndex: "status" },
    ],
    formFields: [
      { name: "name", label: "Account Name", type: "text", required: true },
      { name: "bankName", label: "Bank", type: "text", required: true },
      { name: "accountNo", label: "Account No", type: "text", required: true },
      { name: "iban", label: "IBAN", type: "text" },
      { name: "branch", label: "Branch", type: "text" },
      { name: "swiftCode", label: "Swift Code", type: "text" },
      { name: "displayName", label: "Display Name", type: "text" },
      {
        name: "accountTypes",
        label: "Account Type",
        type: "multiselect",
        options: BANK_ACCOUNT_TYPE_OPTIONS,
        required: true,
      },
      { name: "description", label: "Description", type: "textarea" },
      { name: "status", label: "Status", type: "select", options: STATUS_OPTIONS, defaultValue: "active" },
    ],
  },

  templates: {
    key: "templates",
    label: "Templates",
    description: "Downloadable system templates for bulk uploads",
    disabled: false,
    isCustomPage: true, // TemplatesPage — tile grid with download CTAs
    columns: [],
    formFields: [],
  },
};

/**
 * Get all sections as an array (for the landing page).
 */
export const getSectionList = () => {
  return Object.values(sectionConfigs);
};

/**
 * Get config for a specific section.
 */
export const getSectionConfig = (sectionKey) => {
  return sectionConfigs[sectionKey] || null;
};

export default sectionConfigs;
